import React from 'react'

import { Button, Header, Label, Loader, Message, Segment } from 'semantic-ui-react'

import { IClipIntegrity } from '../../../api/Data'

interface IProps {
  data: IClipIntegrity | null
  loading: boolean
  error: boolean
  recordedAt?: string | Date | null
  onProcess?: () => void
}

const classificationLabels: Record<IClipIntegrity['assessment']['classification'], string> = {
  direct_camera: 'Strong direct-capture evidence',
  likely_camera: 'Camera origin likely',
  edited: 'Editing markers detected',
  recompressed: 'Recompression markers detected',
  unavailable: 'Not yet analysed',
}

const yesNoUnknown = (value: boolean | null) => {
  if (value === null) return 'Not available'
  return value ? 'Yes' : 'No'
}

const formatDuration = (seconds: number | null) => {
  if (seconds === null) return 'Not available'
  if (seconds < 60) return `${seconds.toFixed(1)} seconds`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

const formatRecordedAt = (value: string | Date | null | undefined) => {
  if (!value) return 'Not available'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)

  const pad = (part: number) => String(part).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(
    parsed.getHours()
  )}:${pad(parsed.getMinutes())}`
}

const ClipIntegrityPanel = ({ data, loading, error, recordedAt, onProcess }: IProps) => {
  if (loading) return <Loader active inline="centered" content="Analysing device and media signals..." />

  if (error) {
    return (
      <Message negative>
        <Message.Header>Device information could not be loaded</Message.Header>
        <p>Please try reopening the clip.</p>
      </Message>
    )
  }

  if (!data || !data.available) {
    return (
      <Message>
        <Message.Header>Analysis not available</Message.Header>
        <p>This clip may have been processed before device and integrity analysis was introduced.</p>
        {onProcess && (
          <Button primary size="small" onClick={onProcess}>
            Process analysis
          </Button>
        )}
      </Message>
    )
  }

  const resolution =
    data.media.width && data.media.height ? `${data.media.width} × ${data.media.height}` : 'Not available'

  return (
    <div className="clip-integrity-panel">
      <Segment>
        <Header as="h3">
          Capture evidence{' '}
          <Label color={data.assessment.classification === 'edited' ? 'orange' : 'violet'}>
            {classificationLabels[data.assessment.classification]}
          </Label>
        </Header>
        <dl>
          <dt>Date recorded</dt>
          <dd>{formatRecordedAt(recordedAt || data.capture.recordedAt)}</dd>
          <dt>Source</dt>
          <dd>{data.capture.source}</dd>
          <dt>Device category</dt>
          <dd>{data.capture.deviceCategory}</dd>
          <dt>Recorded using Vloggi camera flow</dt>
          <dd>{data.capture.directCaptureDeclared ? 'Yes' : 'No'}</dd>
          <dt>Camera-origin encoder marker</dt>
          <dd>{data.capture.cameraEncoderDetected ? 'Yes' : 'No'}</dd>
          <dt>Editing software detected</dt>
          <dd>{data.capture.editingSoftwareDetected ? 'Yes' : 'No'}</dd>
          <dt>Recording timestamps consistent</dt>
          <dd>{yesNoUnknown(data.capture.timestampConsistent)}</dd>
        </dl>
      </Segment>

      <Segment>
        <Header as="h3">Media details</Header>
        <dl>
          <dt>Resolution</dt>
          <dd>{resolution}</dd>
          <dt>Codec</dt>
          <dd>{data.media.codec || 'Not available'}</dd>
          <dt>Frame rate</dt>
          <dd>{data.media.frameRate === null ? 'Not available' : `${data.media.frameRate} fps`}</dd>
          <dt>Duration</dt>
          <dd>{formatDuration(data.media.duration)}</dd>
          <dt>Rotation</dt>
          <dd>{data.media.rotation}°</dd>
        </dl>
      </Segment>

      <Message info>
        <Message.Header>What this assessment means</Message.Header>
        <p>{data.assessment.caveat}</p>
        <p>
          The source describes how the clip entered Vloggi. Encoder markers are technical clues in the file, and their
          absence does not mean a clip is synthetic.
        </p>
        <p>Exact device identifiers, location metadata and raw metadata remain private and are not shown here.</p>
      </Message>
    </div>
  )
}

export default ClipIntegrityPanel
